import { AsyncPipe, NgIf } from '@angular/common';
import { Component, inject, input, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ScreenLockService } from '../../core/services/screen-lock.service';
import { AdvancedFilterManager } from '../../shared/components/advanced-filter/advanced-filter-manager';
import { AdvancedFilterComponent } from '../../shared/components/advanced-filter/advanced-filter.component';
import { CountdownManager } from '../../shared/components/countdown/countdown-manager.class';
import { CountdownComponent } from '../../shared/components/countdown/countdown.component';
import { HeaderBreadcrumbManager } from '../../shared/components/header-breadcrumb/header-breadcrumb-manager';
import { HeaderBreadcrumbComponent } from '../../shared/components/header-breadcrumb/header-breadcrumb.component';
import { HeaderButtonManager } from '../../shared/components/header-button/header-button-manager';
import { HeaderButtonComponent } from '../../shared/components/header-button/header-button.component';
import { PageViewAndEditModeSwitchManager } from '../../shared/components/page-view-and-edit-mode-switch/page-view-and-edit-mode-switch-manager.class';
import { PageViewAndEditModeSwitchComponent } from '../../shared/components/page-view-and-edit-mode-switch/page-view-and-edit-mode-switch.component';
import { SlideshowHeaderControllerComponent } from '../../shared/components/slideshow-header-controller/slideshow-header-controller.component';
import { PageHeaderService } from './page-header.service';

export interface IPageHeader {
  advancedFilterManager?: AdvancedFilterManager | null;
  countdownManager?: CountdownManager | null;
  icon: string;
  iconBackCommands?: (string | number)[] | null;
  pageViewAndEditModeSwitchManager?: PageViewAndEditModeSwitchManager | null;
  headerButtonManager?: HeaderButtonManager | null;
  headerBreadcrumbManager?: HeaderBreadcrumbManager | null;
  showSlideshowController?: boolean;
  subtitle?: string;
  title: string;
}

@Component({
  imports: [
    MatIcon,
    NgIf,
    AsyncPipe,
    MatMiniFabButton,
    MatIconButton,
    TranslateModule,
    CountdownComponent,
    PageViewAndEditModeSwitchComponent,
    HeaderButtonComponent,
    AdvancedFilterComponent,
    ReactiveFormsModule,
    HeaderBreadcrumbComponent,
    SlideshowHeaderControllerComponent,
    MatTooltip,
  ],
  selector: 'app-page-header',
  styleUrl: './page-header.component.scss',
  templateUrl: './page-header.component.html',
})
export class PageHeaderComponent implements OnDestroy {
  public hidden = input<boolean>(false);

  private readonly screenLockService = inject(ScreenLockService);

  /**
   * Only the screens that own a lock — a dashboard or slideshow with `autoLock` resolved — show the button;
   * `ScreenLockService` is never started anywhere else, so no other page grows one.
   */
  public readonly isScreenLockRunning = this.screenLockService.isRunning;
  public readonly isScreenLocked = this.screenLockService.isLocked;
  public pageHeader: IPageHeader = { icon: '', title: '' };
  private readonly subscriptions: Subscription[] = [];
  public search: FormControl = new FormControl('');

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly titleService: Title,
  ) {
    this.subscriptions.push(
      this.router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          map((): IPageHeader => this.buildPageHeaderFromActiveRoute()),
        )
        .subscribe((data: IPageHeader): void => {
          PageHeaderService.setPageHeader({});
          this.pageHeader = data;
        }),
      PageHeaderService.headerSetByPage$.subscribe((pageHeader: Partial<IPageHeader>) => {
        this.pageHeader = {
          ...this.pageHeader,
          ...pageHeader,
        };
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription?.unsubscribe());
  }

  private buildPageHeaderFromActiveRoute(): IPageHeader {
    let child = this.router.routerState.snapshot.root;

    while (child.firstChild) {
      child = child.firstChild;
    }

    const title = child.data?.['title'] ? this.translate.instant(child.data?.['title']) : '';

    if (title) {
      this.titleService.setTitle(title);
    }

    return {
      advancedFilterManager: child.data?.['advancedFilterManager'] ?? null,
      countdownManager: child.data?.['countdownManager'] ?? null,
      headerBreadcrumbManager: child.data?.['headerBreadcrumbManager'] ?? null,
      headerButtonManager: child.data?.['headerButtonManager'] ?? null,
      icon: child.data?.['icon'],
      pageViewAndEditModeSwitchManager: child.data?.['pageViewAndEditModeSwitchManager'] ?? null,
      showSlideshowController: child.data?.['showSlideshowController'] ?? false,
      title,
    };
  }

  public onIconClick(): void {
    if (this.pageHeader.iconBackCommands?.length) {
      this.router.navigate(this.pageHeader.iconBackCommands);
    }
  }

  public onLockScreen(): void {
    this.screenLockService.lock();
  }

  public onChangeFilterCardDisplay(): void {
    if (!this.pageHeader.advancedFilterManager) {
      return;
    }

    this.pageHeader.advancedFilterManager.isFilterCardVisible =
      !this.pageHeader.advancedFilterManager.isFilterCardVisible;
  }
}
